import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import type { To } from "react-router";
import IconButton from "../../primitives/components/IconButton/IconButton";
import Text from "../../primitives/components/Text/Text";
import { styles } from "./pageShell.css";

interface PageShellProps {
  /** Title shown next to the back button, on the dark background. */
  title: string;
  /**
   * Where the back button navigates. Defaults to the parent route (".."). Pass
   * `null` to hide the back button entirely.
   */
  backTo?: To | null;
  /** Accessible label for the back button. */
  backAriaLabel?: string;
  /**
   * Whether the sheet scrolls its own overflow. Pass `false` when the body
   * manages its own scrolling (e.g. independently scrolling columns) so the
   * sheet clips instead of scrolling as a whole. Defaults to `true`.
   */
  scrollBody?: boolean;
  /** Body content, rendered inside the white sheet. */
  children: ReactNode;
}

/**
 * Canonical page layout: a dark surface with a header (back button + title) on
 * the black background and a white, rounded sheet for the body. The header
 * stays pinned while the sheet scrolls. Header and sheet share the same width
 * so their edges line up.
 *
 * The component is self-contained — it pins its own light/dark themes and fills
 * the height it's given — so it can be dropped into any shell.
 */
const PageShell = ({
  title,
  backTo = "..",
  backAriaLabel = "Zurück",
  scrollBody = true,
  children,
}: PageShellProps) => {
  const navigate = useNavigate();

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <header className={styles.header}>
          {backTo !== null && (
            <IconButton
              icon="arrow-left"
              ariaLabel={backAriaLabel}
              size="large"
              color="neutral"
              variant="secondary"
              onClick={() => void navigate(backTo)}
            />
          )}
          <Text variant="h4">{title}</Text>
        </header>

        <div
          className={`${styles.sheet} ${
            scrollBody ? styles.scroll : styles.noScroll
          }`}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default PageShell;
